import { FGP } from './eftBuilder';

/**
 * Turning "this impression could not be taken" into the amputation record the EBTS expects.
 *
 * Type-2 field 84 (AMP) names INDIVIDUAL fingers — position 1 through 10. The plain impressions do not
 * appear in it, because they are aggregates: a flat thumb is the same digit as the rolled thumb, and a
 * four-finger slap is four fingers at once.
 *
 * The builder used to handle that by SKIPPING 11–14 outright. On a full ten-print that is invisible —
 * the same digit is marked on its rolled position anyway — but a workflow that collects only the flat
 * thumbs has no rolled thumb to carry the mark, so an amputated thumb produced a file that was simply
 * missing FGP 11 with nothing to say why.
 *
 * Expanding is what the receiving agency expects: a missing flat thumb is recorded as the thumb, and a
 * missing slap as the four fingers it would have contained.
 */

/** Plain/aggregate position -> the individual fingers 2.084 records instead. */
const COVERS: Record<number, number[]> = {
  11: [1],              // right flat thumb  -> right thumb
  12: [6],              // left flat thumb   -> left thumb
  13: [2, 3, 4, 5],     // right slap        -> right index..little
  14: [7, 8, 9, 10],    // left slap         -> left index..little
};

export interface AmpEntry {
  /** Individual finger position, 1–10. */
  fgp: number;
  /** `XX` amputated, `UP` bandaged/unprintable. */
  code: string;
}

/**
 * The AMP entries for a set of marks, in ascending finger order.
 *
 * Deduplicated by finger: a workflow taking both the rolled and the flat thumb can have both marked, and
 * the same digit must not be listed twice. The FIRST mark for a finger wins — they describe one digit, so
 * a disagreement between them is a data problem to leave visible rather than to average away.
 */
export function ampEntries(ampCodes: Record<string, string>): AmpEntry[] {
  const byFinger = new Map<number, string>();
  for (const [scanType, code] of Object.entries(ampCodes)) {
    if (!code) continue;
    const fgp = FGP[scanType];
    if (!fgp) continue; // a half-slap: the stitched slap carries the mark, not its halves
    for (const finger of COVERS[fgp] ?? [fgp]) {
      if (!byFinger.has(finger)) byFinger.set(finger, code);
    }
  }
  return [...byFinger.entries()]
    .sort(([a], [b]) => a - b)
    .map(([fgp, code]) => ({ fgp, code }));
}

/**
 * Impressions that must NOT be sent because the digit they show is recorded as amputated or unprintable.
 *
 * An AMP entry and an image of the same finger contradict each other: the submission says the finger is
 * missing and then encloses a picture of it. So every position covering ONLY a marked digit is dropped —
 * the rolled finger, and the flat thumb, which is the same digit as the rolled thumb.
 *
 * A four-finger slap is the exception, and deliberately: it depicts four digits, so it is only invalid
 * once ALL FOUR are marked. One amputated index does not make an otherwise good slap of the remaining
 * three fingers unsendable, and discarding it would throw away impressions that were successfully taken.
 */
export function imagesToClear(ampCodes: Record<string, string>): Set<string> {
  const marked = new Set(ampEntries(ampCodes).map((e) => e.fgp));
  const out = new Set<string>();
  if (!marked.size) return out;

  for (const [scanType, fgp] of Object.entries(FGP)) {
    const covers = COVERS[fgp] ?? [fgp];
    // Every digit this position shows is marked, so nothing legitimate is left in the picture.
    if (covers.every((d) => marked.has(d))) out.add(scanType);
  }
  return out;
}
